// Cover rendering utilities

/** @typedef {import('../types/jsdoc-types.js').Cover} Cover */
/** @typedef {import('../types/jsdoc-types.js').Point} Point */

import { gridPointToPixel } from './coordinate-transforms.js';

const COVER_COLOR = '#8800ff'; // Purple
const COVER_COLOR_FADED = 'rgba(136, 0, 255, 0.5)';
const SUBDUED_COVER_COLOR = '#663399'; // Darker purple

/**
 * Draw a cover polygon segment
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<Point>} points - Pixel coordinates
 * @param {string} color
 * @param {number} zoom
 * @param {number} opacity
 * @param {boolean} isComplete - Whether to close and fill the polygon
 * @param {boolean} [showCircles=false]
 */
function drawCoverSegment(ctx, points, color, zoom, opacity, isComplete, showCircles = false) {
  if (points.length < 1) return;

  ctx.globalAlpha = opacity;

  // Draw lines between points
  if (points.length > 1) {
    ctx.strokeStyle = color;
    ctx.lineWidth = 6 / zoom;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const first = points[0];
    if (!first) return;
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < points.length; i++) {
      const pt = points[i];
      if (!pt) continue;
      ctx.lineTo(pt.x, pt.y);
    }

    // Close and fill polygon if complete
    if (isComplete) {
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    }

    ctx.stroke();
  }

  // Draw control points as circles
  if (showCircles) {
    ctx.fillStyle = color;
    const circleRadius = 8 / zoom;
    for (const point of points) {
      ctx.beginPath();
      ctx.arc(point.x, point.y, circleRadius, 0, 2 * Math.PI);
      ctx.fill();

      // Black border
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2 / zoom;
      ctx.stroke();
    }
  }

  ctx.globalAlpha = 1.0;
}

/**
 * Draw all covers with selection/hover highlighting
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<Cover>} covers
 * @param {object} options
 * @param {number} options.gridSize
 * @param {number} options.zoom
 * @param {string} options.tool - Current editor tool
 * @param {number|null} [options.selectedCoverIndex=null]
 * @param {number|null} [options.hoveredCoverIndex=null]
 * @param {Array<{x: number, y: number}>|null} [options.currentCover=null]
 * @param {{x: number, y: number}|null} [options.coverPreviewPoint=null]
 * @param {string} [options.coverSubtool='selectCover']
 */
export function drawCovers(ctx, covers, options) {
  const {
    gridSize,
    zoom,
    tool,
    selectedCoverIndex = null,
    hoveredCoverIndex = null,
    currentCover = null,
    coverPreviewPoint = null,
    coverSubtool = 'selectCover',
  } = options;

  const isCoverTool = tool === 'drawCover';

  // Draw all completed covers
  for (let i = 0; i < covers.length; i++) {
    const cover = covers[i];
    if (!cover) continue;

    if (!cover.isComplete || !cover.points || cover.points.length < 3) continue;

    const isSelected = i === selectedCoverIndex;
    const isHovered = i === hoveredCoverIndex;

    /** @type {string} */
    let color;
    let opacity;
    let showCircles;

    if (isCoverTool) {
      if (isSelected || isHovered) {
        color = COVER_COLOR;
        opacity = 0.4;
        showCircles = true;
      } else {
        color = SUBDUED_COVER_COLOR;
        opacity = 0.3;
        showCircles = false;
      }
    } else {
      color = SUBDUED_COVER_COLOR;
      opacity = 0.3;
      showCircles = false;
    }

    const pixelPoints = cover.points.map((p) => gridPointToPixel(p, gridSize));
    drawCoverSegment(ctx, pixelPoints, color, zoom, opacity, true, showCircles);
  }

  // Draw current cover being drawn (only in drawCover subtool)
  if (isCoverTool && coverSubtool === 'drawCover') {
    if (currentCover && currentCover.length > 0) {
      drawCoverSegment(ctx, currentCover, COVER_COLOR_FADED, zoom, 0.5, false, true);

      // Draw preview line from last point to preview point
      if (coverPreviewPoint && currentCover.length > 0) {
        const lastPoint = currentCover[currentCover.length - 1];
        if (!lastPoint) return;

        ctx.strokeStyle = COVER_COLOR_FADED;
        ctx.lineWidth = 6 / zoom;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(coverPreviewPoint.x, coverPreviewPoint.y);
        ctx.stroke();

        // Show dotted closing line if polygon can be closed (3+ points)
        if (currentCover.length >= 3) {
          const firstPoint = currentCover[0];
          if (!firstPoint) return;
          ctx.strokeStyle = 'rgba(136, 0, 255, 0.7)';
          ctx.lineWidth = 4 / zoom;
          ctx.setLineDash([10 / zoom, 10 / zoom]);
          ctx.beginPath();
          ctx.moveTo(coverPreviewPoint.x, coverPreviewPoint.y);
          ctx.lineTo(firstPoint.x, firstPoint.y);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }
    }

    // Draw preview point when not hovering over a cover
    if (coverPreviewPoint && hoveredCoverIndex === null) {
      ctx.fillStyle = COVER_COLOR_FADED;
      const circleRadius = 8 / zoom;
      ctx.beginPath();
      ctx.arc(coverPreviewPoint.x, coverPreviewPoint.y, circleRadius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2 / zoom;
      ctx.stroke();
    }
  }
}
